(() => {
  'use strict';

  if (customElements.get('collection-infinite-scroll')) return;

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement.querySelector('#product-grid');
      this.pagination = this.querySelector('[data-pagination]');
      this.controls = this.querySelector('[data-controls]');
      this.status = this.querySelector('[data-status]');
      this.retry = this.querySelector('[data-retry]');
      this.nextLink = this.querySelector('[data-next-link]');
      this.nextUrl = this.dataset.nextUrl;
      this.page = Number(this.dataset.currentPage);
      this.loading = false;

      // Keep the ordinary page links when automatic loading is unsupported.
      if (!this.grid || !('IntersectionObserver' in window)) return;

      this.pagination.hidden = true;
      this.controls.hidden = false;
      this.onRetry = () => this.loadNextPage();
      this.retry.addEventListener('click', this.onRetry);
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '0px 0px 240px 0px' }
      );
      this.observeNextPage();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.retry?.removeEventListener('click', this.onRetry);
    }

    observeNextPage() {
      if (this.nextUrl) {
        this.observer.observe(this);
      } else {
        this.status.textContent = this.dataset.completeMessage;
        this.nextLink.hidden = true;
        this.retry.hidden = true;
      }
    }

    async loadNextPage() {
      if (this.loading || !this.nextUrl || !this.isConnected || this.parentElement.classList.contains('loading')) return;

      this.loading = true;
      this.observer.disconnect();
      this.retry.hidden = true;
      this.nextLink.hidden = true;
      this.status.textContent = this.dataset.loadingMessage;
      this.grid.setAttribute('aria-busy', 'true');
      const controller = new AbortController();
      this.controller = controller;
      this.timeout = setTimeout(() => controller.abort(), 15000);
      const locationBefore = window.location.href;
      let succeeded = false;

      try {
        const url = new URL(this.nextUrl, locationBefore);
        const expectedPage = this.page + 1;
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname ||
            Number(url.searchParams.get(this.dataset.pageParam)) !== expectedPage) {
          throw new Error('Invalid collection page');
        }
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error('Collection page failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        // Dawn replaces this component on filter/sort changes. An older response
        // must never append products to the replacement grid or a changed query.
        if (!this.isConnected || window.location.href !== locationBefore || this.parentElement.classList.contains('loading')) return;

        const grid = html.getElementById('product-grid');
        const next = html.querySelector('collection-infinite-scroll');
        const pagination = next?.querySelector('[data-pagination]');
        if (!grid || grid.dataset.id !== this.dataset.sectionId || !next ||
            !pagination || next.dataset.sectionId !== this.dataset.sectionId || Number(next.dataset.currentPage) !== expectedPage) {
          throw new Error('Invalid collection response');
        }

        const cards = Array.from(grid.children);
        cards.forEach((card) => {
          // Newly appended cards use the same visible state as Dawn's filters.
          card.classList.add('scroll-trigger--cancel');
          card.querySelectorAll('img').forEach((img) => { img.loading = 'lazy'; });
        });
        this.grid.append(...cards);
        this.page = expectedPage;
        this.nextUrl = next.dataset.nextUrl;
        this.pagination.innerHTML = pagination.innerHTML;
        this.pagination.hidden = true;
        if (this.nextUrl) this.nextLink.href = this.nextUrl;
        this.status.textContent = this.dataset.loadedMessage;
        succeeded = true;
      } catch (error) {
        if (!this.isConnected || window.location.href !== locationBefore || this.parentElement.classList.contains('loading')) return;
        this.status.textContent = this.dataset.errorMessage;
        this.retry.hidden = false;
        this.nextLink.hidden = false;
        this.pagination.hidden = false;
      } finally {
        clearTimeout(this.timeout);
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
        if (succeeded && this.isConnected) this.observeNextPage();
      }
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
